/**
 * Custom hook for managing inspection state and logic
 * Compatible with existing API structure from InspeccionDialog.js
 */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import api, { inspeccionesAPI, solicitudesAPI, uploadAPI, notificacionesContratistasAPI, configuracionGeneralAPI } from '../../utils/api';
import { toast } from 'sonner';

export const useInspeccion = (requerimiento, open) => {
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
  const [inspeccion, setInspeccion] = useState(null);
  const [solicitudes, setSolicitudes] = useState([]);
  const [formDirty, setFormDirty] = useState(false);
  const [platformConfig, setPlatformConfig] = useState({
    company_name: 'IMAGINA',
    platform_name: 'Sistema de Postventa',
    logo_url: null
  });
  const actaInputRef = useRef(null);
  
  // Seal data for digital compliance stamp
  const [sealData, setSealData] = useState(null);
  const [sealLoading, setSealLoading] = useState(false);
  
  // Catálogos state
  const [catalogos, setCatalogos] = useState({
    categorias: [],
    lugares: [],
    items: [],
    fallas: []
  });
  
  // Form state
  const [testigoTipo, setTestigoTipo] = useState('solicitante');
  const [testigo, setTestigo] = useState({
    nombre: '',
    dni: '',
    telefono: '',
    email: ''
  });
  const [datosInspeccion, setDatosInspeccion] = useState({
    fecha: new Date().toISOString().split('T')[0],
    horaInicio: new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Lima' }),
    horaFin: '',
    actaUrl: '',
    actaEstado: ''
  });
  const [programacion, setProgramacion] = useState({
    fecha: '',
    horaInicio: ''
  });
  const [comentario, setComentario] = useState('');
  const [editingSolicitudIndex, setEditingSolicitudIndex] = useState(null);
  const [magicLoading, setMagicLoading] = useState(false);

  // Helper: Parse fotos from JSON or string
  const parseFotos = (fotoUrl) => {
    if (!fotoUrl) return [];
    try {
      const parsed = JSON.parse(fotoUrl);
      return Array.isArray(parsed) ? parsed.map(url => ({ url })) : [{ url: parsed }];
    } catch {
      return [{ url: fotoUrl }];
    }
  };

  // Load catálogos
  const loadCatalogos = useCallback(async () => {
    try {
      const [categoriasRes, lugaresRes, itemsRes, fallasRes] = await Promise.all([
        api.get('/catalogos/categorias'),
        api.get('/catalogos/lugares'),
        api.get('/catalogos/items'),
        api.get('/catalogos/fallas')
      ]);
      setCatalogos({
        categorias: categoriasRes.data || [],
        lugares: lugaresRes.data || [],
        items: itemsRes.data || [],
        fallas: fallasRes.data || []
      });
    } catch (error) {
      console.error('Error loading catalogos:', error);
    }
  }, []);

  // Load platform config
  const loadPlatformConfig = useCallback(async () => {
    try {
      const response = await api.get('/configuracion/general');
      if (response.data) {
        setPlatformConfig({
          company_name: response.data.company_name || 'IMAGINA',
          platform_name: response.data.platform_name || 'Sistema de Postventa',
          logo_url: response.data.logo_url || null,
          aprobar_acta_inspeccion_whatsapp: response.data.aprobar_acta_inspeccion_whatsapp === true,
          aprobar_acta_atencion_whatsapp: response.data.aprobar_acta_atencion_whatsapp === true,
          aprobar_acta_diaria_whatsapp: response.data.aprobar_acta_diaria_whatsapp === true
        });
      }
    } catch (error) {
      console.error('Error loading platform config:', error);
    }
  }, []);

  // Load inspection data
  const loadInspeccion = useCallback(async () => {
    if (!requerimiento?.id) return;
    try {
      const response = await inspeccionesAPI.get(requerimiento.id);
      if (response.data) {
        setInspeccion(response.data);
        setTestigoTipo(response.data.testigo_tipo || 'solicitante');
        setTestigo({
          nombre: response.data.testigo_nombre || '',
          dni: response.data.testigo_dni || '',
          telefono: response.data.testigo_telefono || '',
          email: response.data.testigo_email || ''
        });
        
        // Determine actaUrl based on how the acta was created:
        // - If acta_url exists (PDF uploaded), use that URL directly
        // - If only acta_estado exists (sent by email), use acta-view HTML
        let actaUrl = '';
        if (response.data.acta_url) {
          // PDF was uploaded manually - use direct URL
          actaUrl = response.data.acta_url;
        } else if (response.data.acta_estado) {
          // Acta was sent by email - use HTML view
          const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
          actaUrl = `${backendUrl}/api/inspecciones/${response.data.id}/acta-view`;
        }
        
        setDatosInspeccion({
          fecha: response.data.fecha_inspeccion || new Date().toISOString().split('T')[0],
          horaInicio: response.data.hora_inicio || new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Lima' }),
          horaFin: response.data.hora_fin || '',
          actaUrl: actaUrl,
          actaEstado: response.data.acta_estado || ''
        });
        setProgramacion({
          fecha: response.data.fecha_trabajo_programado || '',
          horaInicio: response.data.hora_trabajo_programado || ''
        });
        setComentario(response.data.comentario_representante || '');
        
        // Fetch seal data if acta is approved
        const acta_estado = response.data.acta_estado || '';
        if (acta_estado.startsWith('aprobado')) {
          setSealLoading(true);
          try {
            const sealResp = await api.get(`/seal/inspeccion/${response.data.id}`);
            if (sealResp.data?.aprobado) {
              setSealData(sealResp.data);
            }
          } catch (sealErr) {
            console.warn('No se pudo obtener seal data:', sealErr);
          } finally {
            setSealLoading(false);
          }
        } else {
          setSealData(null);
        }
      }
    } catch (error) {
      // No inspection exists yet - mark as dirty so user can save immediately
      setFormDirty(true);
    }
  }, [requerimiento?.id]);

  // Load solicitudes from API
  const loadSolicitudes = useCallback(async (forceReload = false) => {
    if (!requerimiento?.id) return;
    
    // If not forcing reload and requerimiento already has solicitudes, use them
    if (!forceReload && requerimiento?.solicitudes?.length > 0) {
      setSolicitudes(requerimiento.solicitudes.map(s => ({
        ...s,
        fotos: parseFotos(s.foto_url),
        isNew: false,
        modified: false
      })));
      return;
    }
    
    // Fetch from API
    try {
      const response = await solicitudesAPI.getOne(requerimiento.id);
      const sols = response.data?.solicitudes || [];
      setSolicitudes(sols.map(s => ({
        ...s,
        fotos: parseFotos(s.foto_url),
        isNew: false,
        modified: false
      })));
    } catch (error) {
      console.error('Error loading solicitudes:', error);
      setSolicitudes([]);
    }
  }, [requerimiento]);

  // Initialize testigo based on type
  const initializeTestigo = useCallback((tipo) => {
    if (!requerimiento) return;
    if (tipo === 'titular') {
      setTestigo({
        nombre: requerimiento.cliente_nombre || '',
        dni: '',
        telefono: requerimiento.cliente_telefono || '',
        email: requerimiento.cliente_email || ''
      });
    } else {
      setTestigo({
        nombre: requerimiento.solicitante_nombre || requerimiento.cliente_nombre || '',
        dni: '',
        telefono: requerimiento.solicitante_telefono || requerimiento.cliente_telefono || '',
        email: requerimiento.solicitante_email || requerimiento.cliente_email || ''
      });
    }
  }, [requerimiento]);

  // Handle testigo type change
  const handleTestigoTipoChange = useCallback((tipo) => {
    setTestigoTipo(tipo);
    initializeTestigo(tipo);
    setFormDirty(true);
  }, [initializeTestigo]);

  // Check if ALL solicitudes have evaluacion = 'no_procede'
  const todasNoProcede = useMemo(() => {
    if (solicitudes.length === 0) return false;
    return solicitudes.every(sol => sol.evaluacion === 'no_procede');
  }, [solicitudes]);

  // Form validation
  // State to track if all evaluations are saved in DB
  const [evaluacionesSaved, setEvaluacionesSaved] = useState(false);

  // Check if all solicitudes have valid evaluation
  const checkAllEvaluated = useCallback(() => {
    if (solicitudes.length === 0) return false;
    return solicitudes.every(sol => 
      sol.evaluacion && 
      sol.evaluacion !== 'sin_evaluacion'
    );
  }, [solicitudes]);

  // Update evaluacionesSaved when solicitudes change from DB load
  useEffect(() => {
    // Only set as saved if all have valid evaluation and none are new/modified
    const allEvaluated = solicitudes.length > 0 && solicitudes.every(sol => 
      sol.evaluacion && 
      sol.evaluacion !== 'sin_evaluacion' && 
      !sol.isNew
    );
    // Check if any solicitud was modified locally
    const anyModified = solicitudes.some(sol => sol.modified);
    setEvaluacionesSaved(allEvaluated && !anyModified);
  }, [solicitudes]);

  const canSave = useCallback(() => {
    return !!(testigo.nombre && datosInspeccion.fecha && datosInspeccion.horaInicio);
  }, [testigo.nombre, datosInspeccion.fecha, datosInspeccion.horaInicio]);

  // Dirty-tracking wrappers for UI-exposed setters
  const setTestigoTracked = useCallback((val) => { setTestigo(val); setFormDirty(true); }, []);
  const setDatosInspeccionTracked = useCallback((val) => { setDatosInspeccion(val); setFormDirty(true); }, []);
  const setProgramacionTracked = useCallback((val) => { setProgramacion(val); setFormDirty(true); }, []);
  const setComentarioTracked = useCallback((val) => { setComentario(val); setFormDirty(true); }, []);

  const isFormComplete = useCallback(() => {
    const baseComplete = testigo.nombre && datosInspeccion.fecha && datosInspeccion.horaInicio;
    if (todasNoProcede) return baseComplete;
    return baseComplete && programacion.fecha && programacion.horaInicio;
  }, [testigo.nombre, datosInspeccion.fecha, datosInspeccion.horaInicio, programacion.fecha, programacion.horaInicio, todasNoProcede]);

  // Can send email or print only if form is complete AND all solicitudes have evaluation saved in DB
  const canSendOrPrint = useCallback(() => {
    return isFormComplete() && evaluacionesSaved;
  }, [isFormComplete, evaluacionesSaved]);

  const canTerminar = useCallback(() => {
    if (todasNoProcede) {
      // En flujo "no procede": necesita acta adjunta o correo enviado + evaluaciones guardadas
      const hasActa = datosInspeccion.actaUrl || 
        ['enviado_correo', 'aprobado_correo', 'rechazado_correo', 'enviado_whatsapp', 'aprobado_whatsapp', 'rechazado_whatsapp'].includes(datosInspeccion.actaEstado);
      return isFormComplete() && hasActa && evaluacionesSaved;
    }
    const hasRealFile = datosInspeccion.actaUrl && !datosInspeccion.actaUrl.includes('/acta-view');
    return isFormComplete() && hasRealFile && evaluacionesSaved;
  }, [isFormComplete, datosInspeccion.actaUrl, datosInspeccion.actaEstado, todasNoProcede, evaluacionesSaved]);

  // ============ Actions ============

  const handleSave = useCallback(async (onUpdate) => {
    setLoading(true);
    try {
      const data = {
        requerimiento_id: requerimiento.id,
        testigo_tipo: testigoTipo,
        testigo_nombre: testigo.nombre,
        testigo_dni: testigo.dni,
        testigo_telefono: testigo.telefono,
        testigo_email: testigo.email,
        fecha_inspeccion: datosInspeccion.fecha,
        hora_inicio: datosInspeccion.horaInicio,
        hora_fin: datosInspeccion.horaFin,
        acta_url: datosInspeccion.actaUrl,
        acta_estado: datosInspeccion.actaEstado,
        fecha_trabajo_programado: programacion.fecha,
        hora_trabajo_programado: programacion.horaInicio,
        comentario_representante: comentario
      };

      if (inspeccion) {
        await inspeccionesAPI.update(inspeccion.id, data);
      } else {
        const response = await inspeccionesAPI.create(data);
        setInspeccion(response.data);
      }

      // Save solicitudes changes
      let savedCount = 0;
      let skippedCount = 0;
      
      for (const sol of solicitudes) {
        if (sol.isNew) {
          // Validate required fields before saving
          if (!sol.categoria || !sol.lugar || !sol.item || !sol.falla || !sol.descripcion) {
            console.warn('Skipping solicitud with missing required fields:', sol);
            skippedCount++;
            continue;
          }
          
          // Create new solicitud
          const fotoUrls = sol.fotos?.map(f => f.url || f) || [];
          try {
            await solicitudesAPI.addItem(requerimiento.id, {
              categoria: sol.categoria,
              lugar: sol.lugar,
              item: sol.item,
              falla: sol.falla,
              descripcion: sol.descripcion,
              evaluacion: sol.evaluacion || 'sin_evaluacion',
              foto_url: fotoUrls.length > 0 ? JSON.stringify(fotoUrls) : null,
              proveedor_id: sol.proveedor_id || null
            });
            savedCount++;
          } catch (err) {
            console.error('Error saving new solicitud:', err);
          }
        } else if (sol.modified) {
          const fotoUrls = sol.fotos?.map(f => f.url || f) || [];
          await uploadAPI.updateSolicitudItem(requerimiento.id, sol.id, {
            categoria: sol.categoria,
            lugar: sol.lugar,
            item: sol.item,
            falla: sol.falla,
            descripcion: sol.descripcion,
            evaluacion: sol.evaluacion,
            foto_url: fotoUrls.length > 0 ? JSON.stringify(fotoUrls) : null,
            proveedor_id: sol.proveedor_id || null
          });
        }
      }

      // Reload solicitudes from DB to get fresh data with correct flags
      await loadSolicitudes(true);

      if (skippedCount > 0) {
        toast.warning(`Inspección guardada. ${skippedCount} solicitud(es) no se guardaron por campos incompletos.`);
      } else {
        toast.success('Inspección guardada correctamente');
      }
      if (onUpdate) onUpdate();
      setFormDirty(false);
      return true;
    } catch (error) {
      console.error('Error saving inspection:', error);
      toast.error('Error al guardar la inspección');
      return false;
    } finally {
      setLoading(false);
    }
  }, [requerimiento?.id, inspeccion, testigoTipo, testigo, datosInspeccion, programacion, comentario, solicitudes, loadSolicitudes]);

  const handleAddSolicitud = useCallback(() => {
    setSolicitudes(prev => [...prev, {
      id: `new_${Date.now()}`,
      categoria: '',
      lugar: '',
      item: '',
      falla: '',
      descripcion: '',
      evaluacion: 'sin_evaluacion',
      fotos: [],
      isNew: true
    }]);
    setEditingSolicitudIndex(solicitudes.length);
    setFormDirty(true);
  }, [solicitudes.length]);

  const handleDeleteSolicitud = useCallback((index) => {
    const sol = solicitudes[index];
    if (sol.isNew) {
      setSolicitudes(prev => prev.filter((_, i) => i !== index));
      setFormDirty(true);
    } else {
      toast.error('Solo se pueden eliminar solicitudes nuevas');
    }
  }, [solicitudes]);

  const handleActaUpload = useCallback(async (e, onSave) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset the input to allow uploading the same file again
    e.target.value = '';

    if (file.type !== 'application/pdf') {
      toast.error('Solo se permiten archivos PDF');
      return;
    }

    setLoading(true);
    try {
      let inspeccionId = inspeccion?.id;
      
      // First save the inspection if not exists
      if (!inspeccionId) {
        await onSave();
        // After save, we need to get the inspection ID
        if (requerimiento?.id) {
          const inspResponse = await inspeccionesAPI.get(requerimiento.id);
          if (inspResponse.data?.id) {
            inspeccionId = inspResponse.data.id;
            setInspeccion(inspResponse.data);
          }
        }
      }
      
      if (inspeccionId) {
        const response = await uploadAPI.uploadActa(inspeccionId, file);
        // Use the actual PDF URL returned from the upload, not the acta-view HTML
        const pdfUrl = response.data?.acta_url;
        
        if (pdfUrl) {
          setDatosInspeccion(prev => ({
            ...prev,
            actaUrl: pdfUrl
          }));
          toast.success('Acta subida correctamente');
          setFormDirty(true);
        } else {
          toast.error('Error: No se recibió la URL del archivo');
        }
      } else {
        toast.error('Error: No se pudo obtener el ID de la inspección');
      }
    } catch (error) {
      console.error('Error uploading acta:', error);
      toast.error('Error al subir el acta');
    } finally {
      setLoading(false);
    }
  }, [inspeccion, requerimiento?.id]);

  const handleActaDelete = useCallback(async () => {
    if (!datosInspeccion.actaUrl) return;
    
    if (!window.confirm('¿Está seguro de eliminar el acta de inspección?')) {
      return;
    }

    setLoading(true);
    try {
      if (inspeccion?.id) {
        await inspeccionesAPI.deleteActa(inspeccion.id);
        setDatosInspeccion(prev => ({
          ...prev,
          actaUrl: '',
          actaEstado: ''
        }));
        toast.success('Acta eliminada correctamente');
        setFormDirty(true);
      }
    } catch (error) {
      console.error('Error deleting acta:', error);
      toast.error('Error al eliminar el acta');
    } finally {
      setLoading(false);
    }
  }, [datosInspeccion.actaUrl, inspeccion?.id]);

  const handleSendEmail = useCallback(async (onUpdate) => {
    if (!inspeccion?.id) {
      toast.error('Primero debe guardar la inspección');
      return;
    }

    setSendingEmail(true);
    try {
      // First save the inspection to ensure latest testigo email is persisted
      const saveData = {
        requerimiento_id: requerimiento.id,
        testigo_tipo: testigoTipo,
        testigo_nombre: testigo.nombre,
        testigo_dni: testigo.dni,
        testigo_telefono: testigo.telefono,
        testigo_email: testigo.email,
        fecha_inspeccion: datosInspeccion.fecha,
        hora_inicio: datosInspeccion.horaInicio,
        hora_fin: datosInspeccion.horaFin,
        acta_url: datosInspeccion.actaUrl,
        acta_estado: datosInspeccion.actaEstado,
        fecha_trabajo_programado: programacion.fecha,
        hora_trabajo_programado: programacion.horaInicio,
        comentario_representante: comentario
      };
      
      await inspeccionesAPI.update(inspeccion.id, saveData);
      
      // Then send the email (backend will read the freshly saved testigo_email)
      const response = await inspeccionesAPI.enviarCorreo(inspeccion.id);
      
      // Update local state to reflect email sent and hora_fin
      if (response.data?.status === 'success') {
        setDatosInspeccion(prev => ({
          ...prev,
          actaEstado: 'enviado_correo',
          horaFin: response.data.hora_fin || prev.horaFin
        }));
        toast.success('Correo enviado correctamente al testigo.');
        
        // Verificar si hay proveedores asignados y si la notificación automática está habilitada
        try {
          const configResponse = await configuracionGeneralAPI.get();
          const notificarAuto = configResponse.data?.notificar_contratistas_auto;
          
          if (notificarAuto) {
            // Verificar si hay proveedores asignados en solicitudes con evaluación "procede"
            const tieneProveedores = solicitudes.some(
              sol => sol.evaluacion === 'procede' && sol.proveedor_id
            );
            
            if (tieneProveedores) {
              // Enviar notificación automática
              const notifResponse = await notificacionesContratistasAPI.notificar(requerimiento.id, true);
              if (notifResponse.data?.success) {
                toast.success(`Notificación automática enviada a ${notifResponse.data.correos_enviados?.length || 0} contratista(s)`);
              }
            }
          }
        } catch (notifError) {
          console.error('Error checking auto notification:', notifError);
          // No mostrar error al usuario ya que el correo principal sí se envió
        }
      } else {
        toast.error(response.data?.message || 'Error al enviar el correo');
      }
      
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error sending email:', error);
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Error al enviar el correo');
      // Actualizar etiqueta local si el acta ya fue aprobada o rechazada
      if (error.response?.status === 400 && detail) {
        if (detail.includes('aprobada')) {
          setDatosInspeccion(prev => ({ ...prev, actaEstado: 'aprobado_correo' }));
        } else if (detail.includes('rechazada')) {
          setDatosInspeccion(prev => ({ ...prev, actaEstado: 'rechazado_correo' }));
        }
      }
    } finally {
      setSendingEmail(false);
    }
  }, [inspeccion?.id, requerimiento?.id, testigoTipo, testigo, datosInspeccion, programacion, comentario, solicitudes]);

  const handleSendWhatsApp = useCallback(async (onUpdate) => {
    if (!inspeccion?.id) {
      toast.error('Primero debe guardar la inspección');
      return;
    }

    // Validar teléfono del testigo
    if (!testigo.telefono) {
      toast.error('El testigo no tiene teléfono registrado. No se puede enviar por WhatsApp.');
      return;
    }

    // Guardar antes de enviar
    setSendingWhatsapp(true);
    try {
      // Guardar datos actualizados
      const updateData = {
        testigo_tipo: testigoTipo,
        testigo_nombre: testigo.nombre,
        testigo_dni: testigo.dni,
        testigo_email: testigo.email,
        testigo_telefono: testigo.telefono,
        fecha_inspeccion: datosInspeccion.fechaInspeccion,
        hora_inicio: datosInspeccion.horaInicio,
        comentario_representante: comentario,
        fecha_trabajo_programado: programacion.fecha || null,
        hora_trabajo_programado: programacion.horaInicio || null,
      };
      // Update evaluaciones (usar endpoint de items, no de requerimientos)
      for (const sol of solicitudes) {
        if (sol.id && !sol.isNew && sol.evaluacion) {
          await uploadAPI.updateSolicitudItem(requerimiento.id, sol.id, { evaluacion: sol.evaluacion });
        }
      }
      await inspeccionesAPI.update(inspeccion.id, updateData);

      // Enviar por WhatsApp
      const response = await inspeccionesAPI.enviarWhatsapp(inspeccion.id);

      if (response.data?.success) {
        setDatosInspeccion(prev => ({
          ...prev,
          actaEstado: 'enviado_whatsapp',
          horaFin: response.data.hora_fin || prev.horaFin
        }));
        if (response.data.sendpulse_success) {
          toast.success(`Acta enviada por WhatsApp a ${response.data.destinatario} (${response.data.telefono})`);
        } else {
          toast.warning(`Acta registrada como enviada por WhatsApp, pero SendPulse reportó un error. Verifique la configuración de la plantilla.`);
        }
      } else {
        toast.error(response.data?.message || 'Error al enviar por WhatsApp');
      }

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error sending WhatsApp:', error);
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Error al enviar por WhatsApp');
    } finally {
      setSendingWhatsapp(false);
    }
  }, [inspeccion?.id, requerimiento?.id, testigoTipo, testigo, datosInspeccion, programacion, comentario, solicitudes]);

  const handleTerminar = useCallback(async (onUpdate, onClose) => {
    if (!inspeccion?.id) {
      toast.error('Primero debe guardar la inspección');
      return;
    }

    setLoading(true);
    try {
      const response = await inspeccionesAPI.terminar(inspeccion.id);
      setDatosInspeccion(prev => ({
        ...prev,
        horaFin: response.data.hora_fin
      }));
      
      const nuevoEstado = response.data?.nuevo_estado;
      if (nuevoEstado === 'terminado') {
        toast.success('Requerimiento terminado desde inspección (todas las solicitudes "No procede")');
      } else {
        toast.success('Inspección finalizada. Estado actualizado a "Atención Pendiente"');
      }
      if (onUpdate) onUpdate();
      if (onClose) onClose();
    } catch (error) {
      toast.error('Error al finalizar la inspección');
    } finally {
      setLoading(false);
    }
  }, [inspeccion?.id]);

  const updateSolicitud = useCallback((index, field, value) => {
    setSolicitudes(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value, modified: true };
      return updated;
    });
    setFormDirty(true);
  }, []);

  // Initialize on dialog open
  useEffect(() => {
    if (open && requerimiento) {
      loadInspeccion();
      loadSolicitudes();
      loadPlatformConfig();
      loadCatalogos();
      initializeTestigo('solicitante');
    }
  }, [open, requerimiento, loadInspeccion, loadSolicitudes, loadPlatformConfig, loadCatalogos, initializeTestigo]);

  const fetchUltimoTestigo = useCallback(async () => {
    if (!requerimiento?.proyecto_id) return;
    setMagicLoading(true);
    try {
      const params = new URLSearchParams({ proyecto_id: requerimiento.proyecto_id });
      if (requerimiento.etapa) params.append('etapa', requerimiento.etapa);
      if (requerimiento.departamento) params.append('departamento', requerimiento.departamento);
      const response = await api.get(`/inspecciones/testigo/ultimo?${params}`);
      const data = response.data;
      if (data) {
        setTestigo({
          nombre: data.testigo_nombre || '',
          dni: data.testigo_dni || '',
          telefono: data.testigo_telefono || '',
          email: data.testigo_email || ''
        });
        setProgramacion({
          fecha: data.fecha_trabajo_programado || '',
          horaInicio: data.hora_trabajo_programado || ''
        });
        setComentario(data.comentario_representante || '');
        toast.success('Datos copiados del último testigo, programación y comentario');
      } else {
        toast.warning('No se encontraron datos previos de testigo, programación ni comentario para este proyecto, etapa y departamento');
      }
    } catch {
      toast.error('Error al buscar datos del último testigo');
    } finally {
      setMagicLoading(false);
    }
  }, [requerimiento?.proyecto_id, requerimiento?.etapa, requerimiento?.departamento]);

  const handleReactivar = useCallback(async (onUpdate, onClose) => {
    if (!inspeccion?.id) return;
    try {
      setLoading(true);
      await api.post(`/inspecciones/${inspeccion.id}/reactivar`);
      toast.success('Inspección reactivada exitosamente');
      if (onUpdate) await onUpdate();
      if (onClose) onClose();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al reactivar la inspección';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [inspeccion?.id]);

  return {
    // State
    loading,
    sendingEmail,
    inspeccion,
    solicitudes,
    platformConfig,
    actaInputRef,
    testigoTipo,
    testigo,
    datosInspeccion,
    programacion,
    comentario,
    editingSolicitudIndex,
    catalogos,
    todasNoProcede,
    formDirty,
    sealData,
    sealLoading,
    
    // Setters (with dirty tracking for UI)
    setTestigo: setTestigoTracked,
    setDatosInspeccion: setDatosInspeccionTracked,
    setProgramacion: setProgramacionTracked,
    setComentario: setComentarioTracked,
    setEditingSolicitudIndex,
    setSolicitudes,
    
    // Actions
    loadInspeccion,
    loadSolicitudes,
    initializeTestigo,
    handleTestigoTipoChange,
    handleSave,
    handleAddSolicitud,
    handleDeleteSolicitud,
    handleActaUpload,
    handleActaDelete,
    handleSendEmail,
    handleSendWhatsApp,
    handleTerminar,
    handleReactivar,
    updateSolicitud,
    fetchUltimoTestigo,
    magicLoading,
    sendingWhatsapp,
    
    // Helpers
    canSave,
    isFormComplete,
    canSendOrPrint,
    canTerminar
  };
};

export default useInspeccion;
